/**
 * PurgeProjectDataDialog — #680 phases 3/4: on-demand, project-scoped
 * client-data purge.
 *
 * Deliberately a separate action from ProjectWorkView's header "Delete
 * Project" (which only archives) — see the issue's own §3: "Separate
 * action, not near Delete... explicitly labelled Purge client data."
 * Mirrors MdmGoldenRecordExplorerView's UnmergeDialog for the typed-
 * confirmation pattern (this codebase's established shape for an
 * irreversible, high-stakes confirmation), extended with the pre-flight
 * receipt the issue's §3 also asks for: per-store counts, fetched from
 * GET .../purge/preview, shown before the confirmation input is even
 * usable.
 */

import { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  X,
  RefreshCw,
  ShieldAlert,
  Loader2,
} from "lucide-react";
import {
  apiv2,
  type PurgePreviewOut,
  type PurgeStoreEntry,
  type PurgeJobOut,
} from "../apiv2";

interface Props {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

const STORE_LABELS: Record<string, string> = {
  mdm: "Master Data Management",
  conversational_ai: "Conversational AI",
  data_converter_final_dq: "Data Converter (final DQ report)",
  semantic_intelligence: "Semantic Intelligence",
  project_knowledge: "Project Knowledge",
  data_profiler_dq_findings: "Data Profiler (DQ findings)",
  data_converter_project_data: "Data Converter (project data)",
  conversational_ai_kb: "Conversational AI (knowledge base)",
  quarantine: "Quarantined uploads",
  upload_object_registry: "Uploaded source files",
  ztd_operational_history: "ZTD operational history",
};

// #903 — a store that did not finish. These make the whole job "failed".
const UNFINISHED_STATUSES = new Set(["failed", "incomplete", "not_attempted"]);

const SKIP_LABELS: Record<string, string> = {
  nothing_to_purge: "Nothing left to purge",
  failed: "Failed — may be partly removed; a re-run retries what is left",
  incomplete: "Incomplete — some data was removed, the rest was kept",
  not_attempted: "Not attempted — a re-run will do it",
  preview_failed: "Could not be counted",
  would_not_attempt:
    "Would not run — a store it depends on is under legal hold",
  skipped_no_policy: "No retention policy for this store",
  skipped_inactive_policy: "Retention policy is inactive",
  skipped_legal_hold: "On legal hold",
  skipped_unsupported_disposal_method: "Policy is not set to delete",
  skipped_unmapped_category: "Not yet wired to a retention category",
  no_preview_available: "No preview available",
};

function storeLabel(key: string): string {
  return STORE_LABELS[key] ?? key;
}

function formatCounts(counts?: Record<string, number>): string {
  if (!counts) return "0";
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const breakdown = Object.entries(counts)
    .map(([k, v]) => `${v} ${k.replace(/_deleted$/, "").replace(/_/g, " ")}`)
    .join(", ");
  return total > 0 ? breakdown : "0";
}

function StoreRow({ entry }: { entry: PurgeStoreEntry }) {
  const willAct = entry.status === "would_purge" || entry.status === "purged";
  const unfinished =
    UNFINISHED_STATUSES.has(entry.status) || entry.status === "preview_failed";
  // An incomplete store did remove something — say how much, then what is left.
  const removed =
    entry.status === "incomplete" && entry.counts
      ? formatCounts(
          Object.fromEntries(
            Object.entries(entry.counts).filter(([k]) =>
              k.endsWith("_deleted"),
            ),
          ),
        )
      : null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          {storeLabel(entry.store)}
        </p>
        {entry.describes && (
          <p className="text-[11px] text-white/40 mt-0.5">{entry.describes}</p>
        )}
        {unfinished &&
          (entry.error_type || entry.interrupted || entry.reason) && (
            <p
              data-testid={`purge-store-error-${entry.store}`}
              className="text-[11px] text-red-300/80 mt-0.5"
            >
              {[entry.error_type, entry.error, entry.interrupted, entry.reason]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
      </div>
      <div className="shrink-0 text-right">
        {willAct ? (
          <span className="text-xs font-mono text-red-300">
            {formatCounts(entry.counts)}
          </span>
        ) : (
          <span
            className={`text-[11px] ${unfinished ? "text-red-300" : "text-white/40"}`}
          >
            {SKIP_LABELS[entry.status] ?? entry.status}
          </span>
        )}
        {removed && (
          <p className="text-[11px] font-mono text-white/40 mt-0.5">
            removed: {removed}
          </p>
        )}
        {entry.retained && (
          <p
            data-testid={`purge-store-retained-${entry.store}`}
            className="text-[11px] text-amber-300/80 mt-0.5"
          >
            kept (still owns data no policy lets this purge delete):{" "}
            {Object.entries(entry.retained)
              .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
              .join(", ")}
          </p>
        )}
      </div>
    </div>
  );
}

type Phase =
  | "loading"
  | "preview"
  | "preview_error"
  | "purging"
  | "purge_error"
  | "incomplete"
  | "done";

export default function PurgeProjectDataDialog({
  projectId,
  projectName,
  onClose,
}: Props) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [preview, setPreview] = useState<PurgePreviewOut | null>(null);
  const [typed, setTyped] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [job, setJob] = useState<PurgeJobOut | null>(null);
  const [certificateError, setCertificateError] = useState<string | null>(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);

  const loadPreview = useCallback(() => {
    setPhase("loading");
    setErrorMessage(null);
    apiv2
      .previewProjectPurge(projectId)
      .then((p) => {
        setPreview(p);
        setPhase("preview");
      })
      .catch((e) => {
        setErrorMessage(
          e instanceof Error ? e.message : "Failed to load purge preview.",
        );
        setPhase("preview_error");
      });
  }, [projectId]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleDownloadCertificate = async () => {
    if (!job) return;
    setDownloadingCertificate(true);
    setCertificateError(null);
    try {
      await apiv2.downloadPurgeCertificate(projectId, job.job_id);
    } catch (e) {
      setCertificateError(
        e instanceof Error ? e.message : "Failed to download certificate.",
      );
    } finally {
      setDownloadingCertificate(false);
    }
  };

  const confirmed = typed.trim() === projectName.trim();
  const willPurgeAnything =
    preview?.stores.some((s) => s.status === "would_purge") ?? false;
  const unfinishedStores = (job?.result?.stores ?? []).filter((s) =>
    UNFINISHED_STATUSES.has(s.status),
  );
  const notAttemptedStores = unfinishedStores.filter(
    (s) => s.status === "not_attempted",
  );

  const handlePurge = async () => {
    if (!confirmed) return;
    setPhase("purging");
    setErrorMessage(null);
    try {
      const { job_id } = await apiv2.purgeProjectData(projectId);
      // Bounded poll, matching BacklogArchitectView's convention. 180 *
      // 2s = 360s, comfortably past the Celery task's own 300s time limit.
      let finished: PurgeJobOut | null = null;
      for (let i = 0; i < 180; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const polled = await apiv2.getPurgeJob(projectId, job_id);
        if (polled.status === "completed" || polled.status === "failed") {
          finished = polled;
          break;
        }
      }
      if (!finished) {
        throw new Error(
          "Purge timed out waiting for a result — it may still be running. Check back shortly.",
        );
      }
      if (finished.status === "failed") {
        // #903: a run where some stores failed still carries the full
        // per-store receipt — show which purged and which did not, rather than
        // collapsing it to one error line. Only a crash before any store ran
        // (no receipt) falls through to the generic error.
        if (finished.result?.stores?.length) {
          setJob(finished);
          setPhase("incomplete");
          return;
        }
        throw new Error(finished.error_detail || "Purge failed.");
      }
      setJob(finished);
      setPhase("done");
    } catch (e) {
      setErrorMessage(
        e instanceof Error ? e.message : "Purge failed. Please try again.",
      );
      setPhase("purge_error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(10,10,18,0.85)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-red-500/25 shadow-2xl max-h-[85vh] overflow-y-auto"
        style={{ background: "rgba(20,18,28,0.97)" }}
      >
        {phase === "incomplete" ? (
          <div
            data-testid="purge-project-incomplete"
            className="p-8 text-center"
          >
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              Purge Incomplete
            </h3>
            <p className="text-sm text-white/50 mb-4">
              {unfinishedStores.length} store(s) did not finish:{" "}
              {unfinishedStores.map((s) => storeLabel(s.store)).join(", ")}.
              {notAttemptedStores.length > 0 && (
                <>
                  {" "}
                  Not attempted:{" "}
                  {notAttemptedStores
                    .map((s) => storeLabel(s.store))
                    .join(", ")}
                  .
                </>
              )}{" "}
              No certificate is issued for an incomplete purge.
            </p>
            <div className="text-left mb-4">
              {job?.result?.stores.map((s) => (
                <StoreRow key={s.store} entry={s} />
              ))}
            </div>
            <p className="mb-6 text-[11px] text-amber-300/80 text-left">
              Run the purge again to retry: stores that already purged cleanly
              are not touched or re-recorded, and a store that kept data retries
              only what is left.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  // A retry is a new destructive action: require the project
                  // name to be typed again.
                  setTyped("");
                  loadPreview();
                }}
                data-testid="purge-project-retry"
                className="px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-sm text-red-300 font-medium transition-colors"
              >
                Review &amp; retry
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/70 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : phase === "done" ? (
          <div data-testid="purge-project-success" className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              Purge Complete
            </h3>
            <p className="text-sm text-white/50 mb-4">
              {job?.result?.full_erasure_applied
                ? "Client data and ZTD's own operational history for this project were purged."
                : "Client data for this project was purged where a retention policy allowed it."}
            </p>
            <div className="text-left mb-6">
              {job?.result?.stores.map((s) => (
                <StoreRow key={s.store} entry={s} />
              ))}
            </div>
            {certificateError && (
              <p className="mb-3 text-[11px] text-red-400 bg-red-500/8 rounded px-3 py-2 border border-red-500/15 text-left">
                {certificateError}
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleDownloadCertificate}
                disabled={downloadingCertificate}
                data-testid="purge-project-download-certificate"
                className="px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/70 transition-colors disabled:opacity-40 flex items-center gap-2"
              >
                {downloadingCertificate ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                Download Certificate
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/70 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                  <ShieldAlert className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-[var(--text-primary)]">
                    Purge Client Data
                  </h3>
                  <p className="text-[11px] text-[var(--text-primary)]">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={phase === "purging"}
                className="text-[var(--text-primary)] hover:text-white/60 transition-colors disabled:opacity-40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Consequences */}
            <div className="mb-4 p-3 rounded-lg bg-red-500/8 border border-red-500/15">
              <p className="text-[11px] text-red-300/80 leading-relaxed">
                This permanently deletes this project&apos;s client data across
                every store with a matching, active, non-held retention policy.
                Stores without one are left untouched — see the breakdown below.
                This is separate from &quot;Delete Project&quot; (which only
                archives) and cannot be reversed.
              </p>
            </div>

            {/* Pre-flight receipt */}
            <div className="mb-4">
              <p className="text-[11px] font-medium text-white/50 mb-1">
                What will happen
              </p>
              {phase === "loading" && (
                <div className="flex items-center gap-2 text-xs text-white/40 py-4 justify-center">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading preview…
                </div>
              )}
              {phase === "preview_error" && (
                <div className="text-[11px] text-red-400 bg-red-500/8 rounded px-3 py-2 border border-red-500/15">
                  {errorMessage}
                  <button
                    onClick={loadPreview}
                    className="ml-2 underline hover:no-underline"
                  >
                    Retry
                  </button>
                </div>
              )}
              {preview && (
                <div
                  data-testid="purge-project-preview"
                  className="rounded-lg bg-[var(--overlay-subtle)] px-3 py-1 max-h-56 overflow-y-auto"
                >
                  {preview.stores.map((s) => (
                    <StoreRow key={s.store} entry={s} />
                  ))}
                </div>
              )}
              {preview && !willPurgeAnything && (
                <p className="text-[11px] text-amber-300/80 mt-2">
                  No store has an eligible retention policy for this project
                  right now — purging will do nothing until one is configured.
                </p>
              )}
            </div>

            {/* Confirmation input */}
            <div className="mb-5">
              <label className="block text-[11px] font-medium text-white/50 mb-2">
                Type the project name to confirm
              </label>
              <div className="relative">
                <input
                  type="text"
                  data-testid="purge-project-confirm-input"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={projectName}
                  disabled={phase === "purging" || phase === "loading"}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--overlay-subtle)] border border-[var(--border-muted)] text-sm font-mono text-[var(--text-primary)] placeholder-white/20 focus:outline-none focus:border-red-500/40 focus:ring-1 focus:ring-red-500/20 transition-colors"
                  autoComplete="off"
                  spellCheck={false}
                />
                {confirmed && (
                  <CheckCircle2 className="absolute right-2.5 top-2.5 w-4 h-4 text-emerald-400" />
                )}
              </div>
            </div>

            {phase === "purge_error" && errorMessage && (
              <p className="mb-3 text-[11px] text-red-400 bg-red-500/8 rounded px-3 py-2 border border-red-500/15">
                {errorMessage}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={phase === "purging"}
                className="flex-1 px-4 py-2 rounded-lg bg-[var(--overlay-subtle)] hover:bg-[var(--overlay-strong)] text-sm text-white/60 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={handlePurge}
                disabled={
                  !confirmed || phase === "purging" || phase === "loading"
                }
                data-testid="purge-project-confirm-submit"
                className="flex-1 px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-sm text-red-300 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {phase === "purging" ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Purging…
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Purge Client Data
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
